/**
 * Workshop showcase — Interactive frame configuration panels.
 *
 * Only 3 frame types need panels: workflow, email, newsletter.
 * All other frames (text, code, search, image, etc.) are result displays —
 * clicking just selects them, footer actions handle copy/comment/expand/delete.
 */

import { WorkflowFrame } from "@/workshop/components/shapes/workflow-frame/workflow-frame";
import { EmailFrame } from "@/workshop/components/shapes/email-frame/email-frame";
import { NewsletterFrame } from "@/workshop/components/shapes/newsletter-frame/newsletter-frame";
import { WorkflowFramePanel } from "@/workshop/components/shapes/workflow-frame/workflow-frame-panel";
import { EmailFramePanel } from "@/workshop/components/shapes/email-frame/email-frame-panel";
import { NewsletterFramePanel } from "@/workshop/components/shapes/newsletter-frame/newsletter-frame-panel";
import { MOCK_PARALLEL, MOCK_RUNS } from "@/workshop/data/mock-workflows";
import { WorkflowDetailsEditable } from "./workflow-details-editable";

// ─── Mock Data ──────────────────────────────────────────────────────────────

const MOCK_NEWSLETTER_SECTIONS = [
  { id: "s1", title: "Introduction" },
  { id: "s2", title: "This Week's Highlights" },
  { id: "s3", title: "Call to Action" },
];

const noop = () => {};

// ─── Helpers ────────────────────────────────────────────────────────────────

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <h2 className="mb-2 mt-10 text-sm font-semibold uppercase tracking-wider text-muted first:mt-0">
      {children}
    </h2>
  );
}

function FramePanelRow({ label, frame, panel }: { label: string; frame: React.ReactNode; panel: React.ReactNode }) {
  return (
    <div data-testid={`frame-panel-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <p className="mb-2 text-xs text-muted">{label}</p>
      <div className="flex items-start gap-4">
        <div className="w-[380px] shrink-0">{frame}</div>
        <div className="shrink-0">{panel}</div>
      </div>
    </div>
  );
}

// ─── Page ───────────────────────────────────────────────────────────────────

export function FramePanelsPage() {
  return (
    <div className="flex flex-col gap-6 p-6" data-testid="frame-panels-page">
      <h1 className="text-lg font-semibold text-primary">Frame Configuration Panels</h1>
      <p className="text-sm text-secondary">
        Only interactive frames open a config panel when clicked.
        Result frames (text, code, search, image, tool, data, etc.)
        just get selected — their footer actions handle everything.
      </p>

      <SectionTitle>Workflow Detail Panel (Editable — Production)</SectionTitle>
      <p className="mb-2 text-xs text-muted">
        This is the editable workflow detail panel as shown in the right panel Details tab.
        Instruction textareas, approval checkboxes, and key-value params are all editable.
      </p>
      <WorkflowDetailsEditable />

      <SectionTitle>Workflow Frame</SectionTitle>
      <FramePanelRow
        label="Workflow name, trigger, and detailed step parameters (tool params, prompts, branches)"
        frame={
          <WorkflowFrame
            title="Competitive Intel Pipeline"
            flowSteps={MOCK_PARALLEL.steps}
            status="done"
            agentName="Denker"
          />
        }
        panel={
          <WorkflowFramePanel
            name="Competitive Intel Pipeline"
            triggerType="cron"
            schedule="0 9 * * MON"
            steps={MOCK_PARALLEL.steps}
            runs={MOCK_RUNS}
            onClose={noop}
          />
        }
      />

      <SectionTitle>Email Frame</SectionTitle>
      <FramePanelRow
        label="Edit recipient, subject, preview body, send or save draft"
        frame={
          <EmailFrame
            to="team@denker.ai"
            subject="Weekly AI Trends Summary"
            body={"Hi team,\n\nHere are this week's top AI developments:\n\n1. New model architectures showing 2x efficiency gains\n2. Open-source tooling improvements\n3. Enterprise adoption accelerating"}
            agentName="Writer"
          />
        }
        panel={
          <EmailFramePanel
            to="team@denker.ai"
            subject="Weekly AI Trends Summary"
            bodyPreview="Hi team, here are this week's top AI developments: new model architectures, open-source tooling, enterprise adoption..."
            onClose={noop}
          />
        }
      />

      <SectionTitle>Newsletter Frame</SectionTitle>
      <FramePanelRow
        label="Edit subject, preview text, manage sections, recipient count, send or schedule"
        frame={
          <NewsletterFrame
            subject="Denker Weekly #12"
            preview="AI trends, product updates, and community highlights"
            bodyPreview="Welcome to this week's edition..."
            agentName="Writer"
          />
        }
        panel={
          <NewsletterFramePanel
            subject="Denker Weekly #12"
            previewText="AI trends, product updates, and community highlights"
            sections={MOCK_NEWSLETTER_SECTIONS}
            recipientCount={142}
            onClose={noop}
          />
        }
      />

      <SectionTitle>Result Frames (No Panel)</SectionTitle>
      <div className="rounded-lg border border-glass-stroke-subtle bg-glass-fill/50 px-4 py-3">
        <p className="text-[11px] text-secondary leading-relaxed">
          These frame types do NOT open a configuration panel when clicked.
          Clicking just selects (highlight border). All actions are on the frame footer:
          copy, comment, expand, delete.
        </p>
        <div className="mt-2 flex flex-wrap gap-1.5">
          {["Text", "Code", "Search", "Image", "Tool", "Data", "Conversation", "Memory", "HTML", "Markdown", "LinkedIn", "Connection", "Question"].map((name) => (
            <span key={name} className="rounded-md bg-glass-fill-heavy px-2 py-0.5 text-[10px] text-muted">
              {name}
            </span>
          ))}
        </div>
      </div>

      <div className="h-12" />
    </div>
  );
}
